import { useFormControl } from "@material-ui/core/FormControl";
import MuiFormLabel from "@material-ui/core/FormLabel";
import { FormLabelTypeMap as MuiFormLabelTypeMap } from "@material-ui/core/FormLabel/FormLabel";
import { OverridableComponent, OverrideProps } from "@material-ui/core/OverridableComponent";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import ErrorIcon from "@material-ui/icons/ErrorRounded";
import LockIcon from "@material-ui/icons/LockRounded";
import clsx from "clsx";
import React from "react";
import { Color } from "../theme";
import { HasChildren, HasTestId } from "../types";

export interface FormLabelTypeMap<PropsT = unknown, ComponentT extends React.ElementType = "label"> {
    props: MuiFormLabelTypeMap<PropsT, ComponentT>["props"] &
        HasTestId & {
            /**
             * Whether the lock icon should be displayed if the field is `disabled`. Defaults to `undefined` which is interpreted as `false`.
             */
            disableLockIcon?: boolean;
            /**
             * Don't display the validation icon. Defaults to `undefined` which is interpreted as `false` (= validation icon is shown if text is not empty depending on `error` property).
             */
            disableValidationIcon?: boolean;
        };
    defaultComponent: MuiFormLabelTypeMap<PropsT, ComponentT>["defaultComponent"];
    classKey: MuiFormLabelTypeMap<PropsT, ComponentT>["classKey"];
}

export type FormLabelProps<ComponentT extends React.ElementType = FormLabelTypeMap["defaultComponent"], PropsT = unknown> = OverrideProps<
    FormLabelTypeMap<PropsT, ComponentT>,
    ComponentT
>;

export const FormLabel = React.forwardRef(
    <ComponentT extends React.ElementType = FormLabelTypeMap["defaultComponent"], PropsT = unknown>(
        { component, disabled, disableLockIcon, disableValidationIcon, error, testId, ...props }: FormLabelProps<ComponentT, PropsT>,
        ref: FormLabelProps["ref"],
    ): React.ReactElement<FormLabelProps<ComponentT, PropsT>> => {
        return (
            <MuiFormLabel
                component={EndAdornmentFormLabel} // use custom component as workaround because `MuiFormLabel` lacks of extensibility here
                data-testid={testId}
                disabled={disabled}
                // use Material UI mechanism that unknown `properties` are forwarded to `component`
                EndAdornmentFormLabelProps={{
                    component,
                    disabled,
                    disableLockIcon,
                    disableValidationIcon,
                    error,
                }}
                error={error}
                ref={ref}
                {...props}
            />
        );
    },
) as OverridableComponent<FormLabelTypeMap>;

type EndAdornmentFormLabelProps<ComponentT extends React.ElementType, PropsT> = HasChildren &
    PropsT & {
        EndAdornmentFormLabelProps: {
            component: ComponentT | undefined;
            disabled: boolean | undefined;
            disableLockIcon: boolean | undefined;
            disableValidationIcon: boolean | undefined;
            error: boolean | undefined;
        };
    };

const EndAdornmentFormLabel = React.forwardRef(
    <ComponentT extends React.ElementType, PropsT>(
        { children, EndAdornmentFormLabelProps, ...props }: EndAdornmentFormLabelProps<ComponentT, PropsT>,
        ref: FormLabelProps["ref"],
    ): React.ReactElement<EndAdornmentFormLabelProps<ComponentT, PropsT>> => {
        const { component: Component = "label", disabled, disableLockIcon, disableValidationIcon, error } = EndAdornmentFormLabelProps;
        const fcs = useFormControl();

        const classes = useStyles();
        return (
            // use custom component as workaround because `MuiFormLabel` lacks of extensibility here
            <Component ref={ref} {...props}>
                {children}
                {(disabled || fcs?.disabled) && !disableLockIcon ? (
                    <LockIcon className={clsx(classes.icon, classes.lockIcon)} />
                ) : (
                    (error || fcs?.error) && !disableValidationIcon && <ErrorIcon className={clsx(classes.icon)} />
                )}
            </Component>
        );
    },
) as <ComponentT extends React.ElementType, PropsT = unknown>(props: EndAdornmentFormLabelProps<ComponentT, PropsT>) => React.ReactElement;

const useStyles = makeStyles(
    createStyles({
        icon: {
            marginBottom: -4,
            marginLeft: 8,
        },
        lockIcon: {
            color: Color.grey4,
        },
    }),
);
